import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import useWorkspaceAndItemFromRoute from 'screens/V3/Workspace/useWorkspaceAndItemFromRoute';
import {
  NavSectionDiv,
  NavSectionLabelDiv,
} from 'kingpin/navigation/NavSection';
import AppNavItem from 'kingpin/navigation/MainNav/AppNavItem';
import SidebarDiv from '../../Common/SideBarDiv';
import Typography from '../../../../../kingpin/atoms/Typography';
import Button from '../../../../../kingpin/atoms/Button';
import NavSideBarContext from '../../../../../contexts/NavSideBarContext';
import EditPinnedAssetsButton from './EditPinnedAssetsButton';
import { V3PrivateIndicator } from '../../../../../screens/V3/Content/V3PrivateIndicator';
import Row from '../../../../../components/Common/Row';
import PermissionGates from '../../../../../components/PermissionGates';
import PERMISSIONS from '../../../../../permissionDefinitions';
import usePopup from '../../../../../hooks/usePopup';
import WorkspacePopupForm from '../../../../../screens/V3/WorkspaceIndex/WorkspacePopupForm';
import { ModalTransition } from '@atlaskit/modal-dialog';
import V3AppAdminsOnly from 'components/V3AppAdminsOnly';
import useAppSideNav from './useAppSideNav';
import { V3SelectedAppContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import Icon from '../../../../../kingpin/atoms/Icon';

const TitleSectionDiv = styled.div`
  border-bottom: 1px solid var(--color-border-tertiary);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 2px;
  padding: 20px 24px 20px 24px;
`;

const SidebarBack = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  margin-left: -2px;
  svg {
    color: var(--color-status-info) !important;
  }
  span {
    color: var(--color-status-info) !important;
  }
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;

const TitleSection = ({ workspace }: { workspace: V3.Workspace }) => {
  const { open, close, isOpen } = usePopup();
  const navigate = useNavigate();
  const onBackClicked = useCallback(() => {
    navigate('/workspaces');
  }, [navigate]);

  const { isOpen: navIsOpen } = React.useContext(NavSideBarContext);

  return (
    <>
      <TitleSectionDiv>
        <Row
          centerAlign
          spaceBetween
          style={{ width: '100%' }}
          className={navIsOpen ? undefined : 'hide-when-collapsed'}
        >
          <SidebarBack onClick={onBackClicked}>
            <Icon
              icon={'arrow-left'}
              height={16}
              width={16}
              color={'var(--color-status-info'}
            />
            <Typography.Body type={'Label xs'}>Workspaces</Typography.Body>
          </SidebarBack>

          <PermissionGates.Has
            requiredPermission={PERMISSIONS.WORKSPACES.CREATE}
          >
            <Button
              size={'Medium'}
              type={'Ghost'}
              iconColor={'var(--color-text-muted)'}
              onClick={open}
              icon={'edit-outline'}
            />
          </PermissionGates.Has>
        </Row>

        <Row
          spaceBetween
          centerAlign
          style={{
            width: '100%',
            gap: 8,
            alignItems: 'baseline',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <Typography.Header type={'H3'} isEllipsis>
            {workspace.label}
          </Typography.Header>
        </Row>

        {!workspace.isPublished && (
          <div style={{ marginTop: '5px' }}>
            <V3PrivateIndicator />{' '}
          </div>
        )}
      </TitleSectionDiv>
      <ModalTransition>
        {isOpen && <WorkspacePopupForm workspace={workspace} close={close} />}
      </ModalTransition>
    </>
  );
};

const EmptyPinnedSection = ({
  section,
}: {
  section: V3.Navigation.SideSection;
}) => {
  if (!section.sectionSettings || !section.sectionSettings.isPinnedSection) {
    return null;
  }

  if (section.items.length !== 0) {
    return null;
  }

  return (
    <div style={{ padding: '0px 8px' }}>
      <Typography.Body
        type="Button Text"
        isEllipsis
        ellipsisPosition="right"
        color={'var(--color-text-disabled)'}
      >
        No Assets Pinned Yet
      </Typography.Body>
    </div>
  );
};

const WorkspaceNavSection = ({
  section,
  workspace,
  children,
}: {
  section: V3.Navigation.SideSection;
  workspace: V3.Workspace;
  children?: JSX.Element | JSX.Element[];
}) => {
  const label = useMemo(() => {
    if (section.title.isEnabled) {
      return section.title.label;
    }
    return undefined;
  }, [section]);

  const hideBorderBottom = useMemo(() => {
    return !section.isBottomBorderEnabled;
  }, [section.isBottomBorderEnabled]);

  return (
    <NavSectionDiv hideBorderBottom={hideBorderBottom}>
      {label && (
        <NavSectionLabelDiv>
          <span>{label}</span>
          <V3AppAdminsOnly>
            <div className={'showOnMouseOver'}>
              <EditPinnedAssetsButton section={section} app={workspace} />
            </div>
          </V3AppAdminsOnly>
        </NavSectionLabelDiv>
      )}
      {children}
    </NavSectionDiv>
  );
};

const WorkspaceSideBar = () => {
  const { selectedWorkspace } = useWorkspaceAndItemFromRoute();
  const { sections, getItems, isAppAdmin } = useAppSideNav(selectedWorkspace);

  if (!selectedWorkspace) {
    return null;
  }

  const isBottomPinned =
    selectedWorkspace.navigationSettings.isBottomSectionPinned ?? false;
  const scrollableSections = isBottomPinned ? sections.slice(0, -1) : sections;
  const bottomSection =
    isBottomPinned && sections.length > 0
      ? sections[sections.length - 1]
      : undefined;

  const renderSection = (section: V3.Navigation.SideSection) => (
    <WorkspaceNavSection
      section={section}
      key={section.key}
      workspace={selectedWorkspace}
    >
      <>
        {getItems(section).map((item) => (
          <AppNavItem
            app={selectedWorkspace}
            item={item}
            key={item.key}
            isAppAdmin={isAppAdmin}
          />
        ))}
        <EmptyPinnedSection section={section} />
      </>
    </WorkspaceNavSection>
  );

  return (
    <V3SelectedAppContext.Provider value={{ selectedWorkspace }}>
      <SidebarDiv>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <TitleSection workspace={selectedWorkspace} />
          {scrollableSections.map(renderSection)}
        </div>
        {bottomSection && (
          <div style={{ borderTop: 'solid 1px var(--color-border-secondary)' }}>
            {renderSection(bottomSection)}
          </div>
        )}
      </SidebarDiv>
    </V3SelectedAppContext.Provider>
  );
};

export default WorkspaceSideBar;
